import get from 'lodash/get';
import { INPUT_TYPE_SELECT, INPUT_TYPE_RADIO, INPUT_TYPE_MULTI_SELECT } from 'common/entities/formNode/inputTypes';
import { NODE_TYPE_QUESTION } from 'common/entities/formNode/nodeTypes';
import { EMPTY_CODE, REPEATED_CODE, EMPTY_LABEL, NO_OPTIONS } from '../constants/errorCodes';
import { isAdditionalGuestInfoNode } from './additionalGuestHelperFunctions';

const CODE = 'code';

export const questionTypesWithOptions = [INPUT_TYPE_SELECT, INPUT_TYPE_RADIO, INPUT_TYPE_MULTI_SELECT];

/**
 * @param {Object} formNode
 * @return {bool}
 */
export const hasEmptyCodeError = (formNode) => {
    if (formNode && formNode.type === NODE_TYPE_QUESTION) {
        return (formNode.code || formNode.name || '').trim().length === 0;
    }
    return false;
};

/**
 * @param {Object} formNode
 * @return {bool}
 */
export const hasEmptyLabelError = (formNode) => {
    if (formNode && formNode.type === NODE_TYPE_QUESTION) {
        return (formNode.label || '').trim().length === 0;
    }
    return false;
};

/**
 * @param {Object} formNode
 * @return {bool}
 */
export const hasNoOptionsError = (formNode) => {
    if (formNode && formNode.type === NODE_TYPE_QUESTION && questionTypesWithOptions.includes(formNode.inputType)) {
        return get(formNode, 'config.options.result', []).length === 0;
    }
    return false;
};

/**
 * Checks if the given formNode or it's parent are inside an additional guest group.
 *
 * @param {Object} formNode
 * @param {Array} allFormNodes
 */
export const isInsideAdditionalGuestGroup = (formNode, allFormNodes) => {
    const parentNode = allFormNodes.find((node) => node.id === formNode.parent);
    const isParentNodeAdditionalGuest = parentNode && isAdditionalGuestInfoNode(parentNode);

    return isAdditionalGuestInfoNode(formNode) || isParentNodeAdditionalGuest;
};

/**
 * Checks if two different question nodes have the same value in the same field.
 *
 * @param {Object} firstNode
 * @param {Object} secondNode
 * @param {string} field LABEL || CODE
 * @return {boolean}
 */
export const haveNodesSameFieldValue = (firstNode, secondNode, field) =>
    firstNode.type === NODE_TYPE_QUESTION && firstNode[field] === secondNode[field] && firstNode.id !== secondNode.id;

/**
 * Checks if a formNode has the same field (label or code) than another node in the form.
 *
 * @param {Object} formNode
 * @param {Object[]} allFormNodes
 * @param {string} field
 * @return {boolean}
 */
export const haveRepeatedNodeValidationErrors = (formNode, allFormNodes, field) => {
    const isNodeInAdditionalGuestGroup = isInsideAdditionalGuestGroup(formNode, allFormNodes);

    return (
        allFormNodes.filter(
            (thisNode) =>
                haveNodesSameFieldValue(thisNode, formNode, field) &&
                (isNodeInAdditionalGuestGroup
                    ? thisNode.parent === formNode.parent
                    : !isInsideAdditionalGuestGroup(thisNode, allFormNodes)),
        ).length > 0
    );
};

/**
 * Checks for repeated code between the given node and the other nodes in the form.
 *
 * @param {Object} formNode
 * @param {Object[]} allFormNodes
 * @return {boolean}
 */
export const hasRepeatedCodeError = (formNode, allFormNodes) =>
    formNode && formNode.type === NODE_TYPE_QUESTION && formNode.code
        ? haveRepeatedNodeValidationErrors(formNode, allFormNodes, CODE)
        : false;

/**
 * This function is temporarily used under the useNameAsQuestionKey feature flag
 * until the feature is released. After that, it'll be the default one.
 *
 * @param {Object} formNode
 * @param {Object[]} allFormNodes
 * @return {string[]} - error codes
 */
export const validateFormNodeWithNameCode = (formNode, allFormNodes, liveErrors = {}) => {
    const emptyCode = hasEmptyCodeError(formNode);
    const repeatedCode = hasRepeatedCodeError(formNode, allFormNodes);
    const emptyLabel = hasEmptyLabelError(formNode);
    const noOption = hasNoOptionsError(formNode);
    const liveErrorList = Object.keys(liveErrors).filter((error) => liveErrors[error]);

    return [
        emptyCode ? EMPTY_CODE : null,
        repeatedCode ? REPEATED_CODE : null,
        emptyLabel ? EMPTY_LABEL : null,
        noOption ? NO_OPTIONS : null,
        ...liveErrorList,
    ].filter(Boolean);
};
